import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { Router } from '@angular/router';
import {
  IntervalMappingService,
  ListRecurringInvoicesByCustomerID,
  ListRecurringInvoicesByMerchantID,
  ListRecurringInvoicesFilters,
  ListRecurringInvoicesDetailedInterval,
  Interval,
  RecurringInvoice,
  RecurringInvoiceService,
  RecurringInvoiceStatus,
} from '@galaxy/billing';
import { Observable, ReplaySubject, combineLatest, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, skip, startWith, takeUntil, withLatestFrom } from 'rxjs/operators';
import { ListRecurringInvoicesDataSource } from './recurring-invoice-table.data-source';
import { MatDialog } from '@angular/material/dialog';
import {
  FilterGroup,
  DateFilterControl,
  CheckboxFilterControl,
  SelectFilterControl,
  NumberInputFilterControl,
  FilterControl,
} from '@vendasta/va-filter2';
import { BusinessSelectorDialogComponent } from '@vendasta/businesses';

const allColumns = ['amount', 'status', 'customer', 'nextIssue', 'frequency'];

@Component({
  selector: 'smb-invoicing-recurring-invoice-table',
  templateUrl: './recurring-invoice-table.component.html',
  styleUrls: ['./recurring-invoice-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RecurringInvoiceTableComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  RecurringInvoiceStatus = RecurringInvoiceStatus;
  intervalMapping;
  @ViewChild('recurringInvoicePaginator', { static: true }) paginator: MatPaginator;

  @Input() identifier: ListRecurringInvoicesByMerchantID | ListRecurringInvoicesByCustomerID;
  @Input() columnIds: string[]; // Columns to display, default: all.

  @Output() customerSelected = new EventEmitter<string>();
  @Output() hasRecurringInvoices = new EventEmitter<boolean>();

  public length$: Observable<number>;
  public dataSource: ListRecurringInvoicesDataSource;
  filterGroup: FilterGroup;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  private inputIdentifier$$: ReplaySubject<ListRecurringInvoicesByMerchantID | ListRecurringInvoicesByCustomerID> =
    new ReplaySubject<ListRecurringInvoicesByMerchantID | ListRecurringInvoicesByCustomerID>(1);
  private inputIdentifier$: Observable<ListRecurringInvoicesByMerchantID | ListRecurringInvoicesByCustomerID> =
    this.inputIdentifier$$.asObservable();

  private filters$: Observable<ListRecurringInvoicesFilters>;
  private frequencyCountControl: NumberInputFilterControl = new NumberInputFilterControl('frequencyCount', 'Count');
  private readonly customFrequencies = new Map<string, Interval>([
    ['Days', Interval.DAY],
    ['Weeks', Interval.WEEK],
    ['Months', Interval.MONTH],
    ['Years', Interval.YEAR],
  ]);
  private frequencyTypeControl: SelectFilterControl = new SelectFilterControl(
    'frequencyInterval',
    'Interval',
    of(this.customFrequencies),
  );

  public displayedColumns = [];

  constructor(
    private recurringInvoiceService: RecurringInvoiceService,
    private router: Router,
    private intervalMappingService: IntervalMappingService,
    private readonly dialog: MatDialog,
  ) {
    this.intervalMapping = this.intervalMappingService.intervalMapping;
    this.dataSource = new ListRecurringInvoicesDataSource(this.recurringInvoiceService);
    this.length$ = this.dataSource.storedRecurringInvoices$.pipe(
      skip(1),
      withLatestFrom(this.dataSource.hasMore$),
      map(([items, hasMore]) => (hasMore ? items.length + this.paginator.pageSize : items.length)),
    );
  }

  ngOnInit(): void {
    this.displayedColumns = this.columnIds || allColumns;

    this.filterGroup = new FilterGroup('recurring-invoices-list')
      .addSection('Status', [
        new CheckboxFilterControl('active', RecurringInvoiceStatus.ACTIVE, false, {
          appliedValueMapper: (name) => ({ name, label: RecurringInvoiceStatus.ACTIVE }),
        }),
        new CheckboxFilterControl('inactive', RecurringInvoiceStatus.INACTIVE, false, {
          appliedValueMapper: (name) => ({ name, label: RecurringInvoiceStatus.INACTIVE }),
        }),
        new CheckboxFilterControl('archived', RecurringInvoiceStatus.ARCHIVED, false, {
          appliedValueMapper: (name) => ({ name, label: RecurringInvoiceStatus.ARCHIVED }),
        }),
      ])
      .addSection('Frequency', [
        new CheckboxFilterControl('weekly', Interval.WEEK, false, {
          appliedValueMapper: (name) => ({ name, label: Interval.WEEK }),
        }),
        new CheckboxFilterControl('monthly', Interval.MONTH, false, {
          appliedValueMapper: (name) => ({ name, label: Interval.MONTH }),
        }),
        new CheckboxFilterControl('yearly', Interval.YEAR, false, {
          appliedValueMapper: (name) => ({ name, label: Interval.YEAR }),
        }),
        new CheckboxFilterControl('custom', 'Custom', false, {
          appliedValueMapper: (name) => ({ name, label: 'Custom' }),
        }),
        this.frequencyCountControl,
        this.frequencyTypeControl,
      ])
      .addSection('Next due date', [
        new DateFilterControl('startDate', 'Start'),
        new DateFilterControl('endDate', 'End'),
      ]);

    this.initFilters();

    combineLatest([this.paginator.page, this.filters$])
      .pipe(takeUntil(this.destroyed$), withLatestFrom(this.inputIdentifier$))
      .subscribe(([[paginator, filters], identifier]) => {
        this.dataSource.updatePage(identifier, paginator.pageSize, paginator.pageIndex, filters);
      });

    combineLatest([this.length$, this.dataSource.loading$, this.filters$])
      .pipe(debounceTime(100))
      .subscribe(([length, loading, filters]) => {
        if (loading) {
          return;
        }
        const filtersApplied =
          filters.statuses.length > 0 || filters.detailedInterval.length > 0 || !!filters.dateFilter;
        const hasRecurringInvoices = length > 0 || filtersApplied;
        this.hasRecurringInvoices.emit(hasRecurringInvoices);
      });
  }

  ngAfterViewInit(): void {
    combineLatest([this.inputIdentifier$, this.filters$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([identifier, filters]) => {
        this.paginator.firstPage();
        this.dataSource.reload(identifier, this.paginator.pageSize, this.paginator.pageIndex, filters);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.inputIdentifier$$.next(changes.identifier.currentValue);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initFilters(): void {
    this.filters$ = this.filterGroup.valueChanges.pipe(
      startWith(this.filterGroup.value),
      debounceTime(300),
      distinctUntilChanged(),
      map(() => {
        const filterValues = this.filterGroup.value;
        const statuses = this.buildStatusFilterValues(
          filterValues.active,
          filterValues.inactive,
          filterValues.archived,
        );
        const frequencies = this.buildFrequencyFilterValues(
          filterValues.weekly,
          filterValues.monthly,
          filterValues.yearly,
          filterValues.custom,
          filterValues.frequencyInterval,
          filterValues.frequencyCount,
        );
        if (filterValues.custom === null || filterValues.custom === false) {
          this.frequencyCountControl.hidden = true;
          this.frequencyTypeControl.hidden = true;
        } else {
          this.frequencyCountControl.hidden = false;
          this.frequencyTypeControl.hidden = false;
        }
        return <ListRecurringInvoicesFilters>{
          statuses: statuses,
          detailedInterval: frequencies,
          dateFilter:
            filterValues.startDate || filterValues.endDate
              ? {
                  dateGte: filterValues.startDate,
                  dateLte: filterValues.endDate,
                }
              : null,
        };
      }),
    );
  }

  buildStatusFilterValues(filterActive, filterInactive, filterArchived: FilterControl): RecurringInvoiceStatus[] {
    const statuses: RecurringInvoiceStatus[] = [];
    if (filterActive) {
      statuses.push(RecurringInvoiceStatus.ACTIVE);
    }
    if (filterInactive) {
      statuses.push(RecurringInvoiceStatus.INACTIVE);
    }
    if (filterArchived) {
      statuses.push(RecurringInvoiceStatus.ARCHIVED);
    }
    return statuses;
  }

  buildFrequencyFilterValues(
    filterWeekly,
    filterMonthly,
    filterYearly,
    filterCustom: FilterControl,
    filterCustomInterval: Interval,
    filterCustomCount: number,
  ): ListRecurringInvoicesDetailedInterval[] {
    const frequencies: ListRecurringInvoicesDetailedInterval[] = [];
    if (filterWeekly) {
      frequencies.push({ interval: Interval.WEEK, intervalCount: 1 });
    }
    if (filterMonthly) {
      frequencies.push({ interval: Interval.MONTH, intervalCount: 1 });
    }
    if (filterYearly) {
      frequencies.push({ interval: Interval.YEAR, intervalCount: 1 });
    }
    if (filterCustom && filterCustomInterval && filterCustomCount) {
      frequencies.push({ interval: filterCustomInterval, intervalCount: filterCustomCount });
    }
    return frequencies;
  }

  public selectRecurringInvoice(i: RecurringInvoice): void {
    this.router.navigateByUrl(`/businesses/accounts/${i.customerId}/recurring-invoices/${i.id}/edit`);
  }

  public createRecurringInvoice(): void {
    if (this.identifier instanceof ListRecurringInvoicesByCustomerID) {
      this.router.navigateByUrl(`/businesses/accounts/${this.identifier.customerId}/recurring-invoices/new`);
    }

    if (this.identifier instanceof ListRecurringInvoicesByMerchantID) {
      const dialog = this.dialog.open(BusinessSelectorDialogComponent, {
        width: '500px',
        data: {
          partnerId: this.identifier.merchantId,
        },
      });
      dialog.afterClosed().subscribe((ag) => {
        if (ag?.accountGroupId) {
          this.router.navigateByUrl(`/businesses/accounts/${ag.accountGroupId}/recurring-invoices/new`);
        }
      });
    }
  }

  public selectCustomer(customerId: string): void {
    this.customerSelected.emit(customerId);
  }

  translateStatus(status: RecurringInvoiceStatus): string {
    switch (status) {
      case RecurringInvoiceStatus.ACTIVE:
        return 'BILLING.INVOICES_PAGE.RECURRING.STATUS_BADGE.ACTIVE';
      case RecurringInvoiceStatus.INACTIVE:
        return 'BILLING.INVOICES_PAGE.RECURRING.STATUS_BADGE.INACTIVE';
      case RecurringInvoiceStatus.ARCHIVED:
        return 'BILLING.INVOICES_PAGE.RECURRING.STATUS_BADGE.ARCHIVED';
      default:
        return '';
    }
  }
}
